import { useState, useRef, useEffect } from 'react'

import axios from 'axios'

import { NOOP } from 'Utils/stableDefaults'

const initialState = {
  isActive: false,
  isUploading: false,
}

function useImageSearch({ onActiveChange = NOOP } = {}) {
  const [state, setState] = useState(initialState)
  const stateRef = useRef(state.isActive)
  const wrapperRef = useRef(null)
  const triggerRef = useRef(null)

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside)

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  // An upload sets isUploading (and leaves the popover isActive) right before a full-page navigation.
  // Returning via the browser back/forward cache restores the page with that state frozen, leaving a
  // stuck spinner and an unresponsive trigger. pageshow fires on bfcache restore (event.persisted); reset.
  useEffect(() => {
    const handlePageShow = (event) => {
      if (event.persisted) {
        stateRef.current = false
        setState(initialState)
        onActiveChange(false)
      }
    }

    window.addEventListener('pageshow', handlePageShow)
    return () => window.removeEventListener('pageshow', handlePageShow)
  }, [])

  function triggerImageSearch(e) {
    // close if popover is open and wrapper element doesn't have click target

    if (!state.isActive) { // popover is closed
      openImageSearch()
    } else {
      const wrapper = wrapperRef.current

      if (wrapper && !wrapper.contains(e.target) && stateRef.current) {
        closeImageSearch()
      }
    }
  }

  function closeImageSearch() {
    stateRef.current = false

    setState(prevState => ({
      ...prevState,
      isActive: false,
    }))
    onActiveChange(false)
  }

  function openImageSearch() {
    stateRef.current = true

    setState(prevState => ({
      ...prevState,
      isActive: true,
    }))
    onActiveChange(true)
  }

  function handleClickOutside(e) {
    const trigger = triggerRef.current

    if (trigger && !trigger.contains(e.target) && stateRef.current) {
      e.preventDefault()
      e.stopPropagation()
      return closeImageSearch()
    }
  }

  function handleUploadStart() {
    setState(prevState => ({
      ...prevState,
      isUploading: true,
    }))
  }

  function jumpToSearch(id) {
    const url = `/3d-models?image_id=${id}`

    return window.location.href = url
  }

  function handleUploadComplete({ data }) {
    const { imageId } = data || {}

    jumpToSearch(imageId)
  }

  function handleUploadError(error) {
    if (window.Sentry) {
      window.Sentry.captureException(error)
    }

    if (window.showNotification) { // fast page notification
      window.showNotification('Something went wrong, please try again', {
        type: 'error',
        autohide: true,
      })
    } else { // slow page notification
      App.events.publish('app:notification:flash:error', 'Something went wrong, please try again')
    }

    setState(prevState => ({
      ...prevState,
      isUploading: false,
    }))
  }

  function handleFileDrop(acceptedFiles) {
    const haveAcceptedFiles = acceptedFiles && acceptedFiles.length > 0

    if (haveAcceptedFiles) {
      const file = acceptedFiles.shift()
      const formData = new FormData()

      handleUploadStart()
      formData.append('file', file)
      postFile(formData)
    }
  }

  function postFile(data) {
    const path = '/api/internal/users/upload_search_image'
    const metaToken = document.getElementsByName('csrf-token')[0]
    const config = {
      headers: { 'X-CSRF-Token': metaToken.getAttribute('content') },
    }

    axios.post(path, data, config)
    .then(response => {
      handleUploadComplete(response)
    })
    .catch(error => {
      handleUploadError(error)
    })
  }


  const { isActive, isUploading } = state

  return {
    // refs
    wrapperRef,
    triggerRef,
    // values
    isActive,
    isUploading,
    // actions
    triggerImageSearch,
    handleFileDrop,
  }
}

export { useImageSearch }
